<template>
    <div class="page-customized">
        <v-progress-linear
            v-if="overlay"
            absolute
            indeterminate
        ></v-progress-linear>

        <app-bar
            :title="externalContent.exists() ? t('external_content_title_update') : t('external_content_title_create')"
            back="/external-contents"
        />

        <v-layout class="mt-n8">
            <v-card
                width="100%"
                color="transparent"
                elevation="0"
                class="pb-8"
            >
                <v-card-text class="pb-0">
                    <v-form class="mt-6">
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('external_content_label_name') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="externalContent.name"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('name')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="externalContent.name"
                                    :items="externalContent.translations['name']"
                                    @touch="(v) => externalContent.translations['name'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('external_content_label_type') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="externalContent.type"
                                    :items="externalContentTypes()"
                                    required
                                    outlined
                                    append-icon="mdi-chevron-down"
                                    dense
                                    :error-messages="form.errors.get('type')"
                                ></v-select>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('external_content_label_url') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="externalContent.url"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                ></v-text-field>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('external_content_label_description') }}</div>
                            <div class="horizontal-input">
                                <tinymce
                                    key="description"
                                    v-model="externalContent.description"
                                ></tinymce>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="externalContent.description"
                                    :items="externalContent.translations['description']"
                                    @touch="(v) => externalContent.translations['description'] = v"
                                    editor
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('general_action_published') }}</div>
                            <div class="horizontal-input">
                                <v-switch
                                    class="ma-0"
                                    v-model="externalContent.published"
                                ></v-switch>
                            </div>
                        </div>
                    </v-form>
                </v-card-text>

                <v-card-actions class="px-4 pt-8">
                    <v-btn
                        :to="{ name: 'external-contents' }"
                        class="mr-4"
                        color="primary"
                    >{{ t('general_action_cancel') }}</v-btn>
                    <v-btn
                        :loading="form.busy"
                        :disabled="form.busy"
                        color="orange"
                        @click.prexternalContent="submit"
                    >{{ externalContent.exists() ? t('general_action_save') : t('external_content_title_create') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import moment from 'moment/moment'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import Tinymce from '@/components/Tinymce.vue'
    import { ExternalContent } from '@/core/models/external-content'

    export default defineComponent({
        components: {
            EditTranslationsModal,
            ConfirmModal,
            Tinymce,
        },

        /**
         * Component constructor.
         *
         * @param props
         */
        setup(props) {
            const store = useStore()
            const router = useRouter()
            const id = router.currentRoute.params['id'] || undefined
            const captured = router.currentRoute.query['id'] || undefined

            const enums = useEnums()
            const i18n = useI18n()

            const externalContentModel = computed(() => <ExternalContent>store.getters['externalContent/get'])

            const state = reactive({
                overlay: true
            })

            const capture = () => {
                store.dispatch(
                    'externalContent/getOrNew',
                    {
                        id: (id || captured),
                        payload: { append: 'translations' }
                    }).then(() => {
                        state.overlay = false
                    })
            }

            const form = reactive(useForm())

            const submit = () => {
                if (captured) {
                    externalContentModel.value.id = <any>undefined
                }
                form.usingSubmit(store.dispatch('externalContent/updateOrCreate', {
                    payload: externalContentModel.value.payload()
                }))
                    .then(() => {
                        const id = <any>externalContentModel.value.id
                        const next = () => router.push({ name: 'external-contents.view', params: <any>{ id: id } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        next()
                    })
            }

            onMounted(() => {
                capture()
            })

            return {
                form,
                capture,
                submit,
                externalContent: externalContentModel.value,
                moment,
                ...enums,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
